import React, { forwardRef, useImperativeHandle } from "react";
import OutsideClickHandler from "react-outside-click-handler";

import { trackingConst } from "../../constants";
import { useAnalytics } from "../../utils/analytics";
import BusinessUnitSelector from "./businessUnitSelector";
import SelectorButton from "./selectorButton";
import { StyledBusinessAccountSelector } from "./styled";
import * as Types from "./types";

type RefHandler = object;

// wrap with forwardRef to allow call closeSelector function from parent component:
const BusinessAccountSelector = forwardRef<RefHandler, Types.BusinessAccountSelector>(
  (
    {
      businessAccounts,
      businessUnits,
      currentBusinessUnit,
      publicProfileUrl,
      selectedBusinessAccountId,
      showBusinessUnitConsents,
      isTrustpilotEmployee,
      toggleOverlayVisibility,
      canManageProfile,
      isMenuExpandOnMobile,
      isCollapsed,
      setIsCollapsed,
    },
    ref,
  ) => {
    const analytics = useAnalytics();
    const [isSelectorVisible, setSelectorVisibility] = React.useState<boolean>(false);

    const toggleSelector = React.useCallback(() => {
      if (!isSelectorVisible) {
        analytics.track(trackingConst.events.buttonClicked, {
          context: trackingConst.contexts.accountSelector,
          name: trackingConst.names.accountSelectorOpened,
          placement: "sidebar",
        });
      }
      toggleOverlayVisibility(!isSelectorVisible);
      setSelectorVisibility(!isSelectorVisible);
    }, [isSelectorVisible, analytics]);

    const closeSelector = React.useCallback(() => {
      if (isSelectorVisible) {
        toggleSelector();
      }
    }, [isSelectorVisible, toggleSelector]);

    useImperativeHandle(ref, () => ({
      closeAccountSelector() {
        closeSelector();
      },
    }));

    const selectedAccount = businessAccounts.find(({ id }) => id === selectedBusinessAccountId);
    const company = selectedAccount ? selectedAccount.name : "";
    const domain = currentBusinessUnit.name ? currentBusinessUnit.name.identifying : "";

    return (
      <StyledBusinessAccountSelector id="business-account-selector">
        <OutsideClickHandler onOutsideClick={closeSelector}>
          <SelectorButton
            company={company}
            domain={domain}
            isInReadOnlyMode={currentBusinessUnit.access.readOnly}
            isSelectorVisible={isSelectorVisible}
            showBusinessUnitConsents={showBusinessUnitConsents}
            toggleSelector={toggleSelector}
            isCollapsed={isCollapsed}
            setIsCollapsed={setIsCollapsed}
          />
          {isSelectorVisible && (
            <BusinessUnitSelector
              businessAccounts={businessAccounts}
              selectedBusinessAccountName={company}
              businessUnits={businessUnits}
              closeSelector={closeSelector}
              currentBusinessUnit={currentBusinessUnit}
              selectedBusinessAccountId={selectedBusinessAccountId}
              showBusinessUnitConsents={showBusinessUnitConsents}
              isTrustpilotEmployee={isTrustpilotEmployee}
              publicProfileUrl={publicProfileUrl}
              canManageProfile={canManageProfile}
              isMenuExpandOnMobile={isMenuExpandOnMobile}
            />
          )}
        </OutsideClickHandler>
      </StyledBusinessAccountSelector>
    );
  },
);

export default BusinessAccountSelector;
